import * as React from 'react';
import {
  Box,
  Card,
  Typography,
  InputBase,
  IconButton,
  Divider,
  Menu,
  MenuItem,
  CircularProgress
} from '@mui/material'

import SendIcon from '@mui/icons-material/Send';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';

import { useSelector, useDispatch } from 'react-redux';
import { utils, writeFileXLSX } from 'xlsx';
import SimpleBar from 'simplebar-react';

import Toolbar from '../components/Toolbar'
import SourceTree from '../components/SourceTree'
import DataTable from '../components/DataTable';
import Visualization from '../components/Visualization';
import PivotTable from '../components/PivotTable';

import { CSettingButton } from '../components/common/Modal';

import TitleModal from '../modals/TitleModal';
import QueryModal from '../modals/QueryModal';
import PptModal from '../modals/PptModal';

import { setSession, getHierarchy, getHistory, getCubeHistory, formatHistory, formatSession, updateHistory, saveSession, renameSession, removeSession, chatWithSQL, parseQuery } from '../slices/chat';
import { getDbAuthByName, cleanupExpiredCredentials } from '../utils/credentialManager';

export default function Chat() {
  const hierarchy = useSelector(state => state.chat.hierarchy)
  const history = useSelector(state => state.chat.history)
  const source_name = useSelector(state => state.chat.source)
  const sourceType = useSelector(state => state.chat.sourceType)
  const isAdminLevel = useSelector(state => state.chat.isAdminCredential)
  const session_name = useSelector(state => state.chat.session)
  const cube_name = useSelector(state => state.chat.cube)

  const [loading, setLoading] = React.useState(false);
  const [hLoading, setHLoading] = React.useState(false);
  const [sessionLoading, setSessionLoading] = React.useState(false);
  const [prompt, setPrompt] = React.useState("");

  const [contextMenu, setContextMenu] = React.useState(null);
  const [contextData, setContextData] = React.useState(null);
  const [actionMenu, setActionMenu] = React.useState(null);

  const [queryOpened, setQueryOpened] = React.useState(false);
  const [pptOpened, setPptOpened] = React.useState(false);
  const [openSession, setOpenSession] = React.useState(false);
  const [updateSession, setUpdateSession] = React.useState(false);

  const dispatch = useDispatch();

  React.useEffect(() => {
    // Clean up expired credentials on component mount
    cleanupExpiredCredentials();
    
    setHLoading(true);
    dispatch(getHierarchy())
      .then(res => setHLoading(false))
      .catch(err => setHLoading(false))
    ;  
  }, [dispatch]);  

  const handleChangeSession = (source, sourceType, session, cube, isAdminCredential) => {
    dispatch(setSession({source, sourceType, session, cube, isAdminCredential}));
    if(session === '') 
      dispatch(formatHistory())
    else {
      setSessionLoading(true);
      // For Oracle EPM sources, use cube parameter; otherwise use regular getHistory
      if (cube) {
        dispatch(getCubeHistory({source, cube, session}))
          .then(res => setSessionLoading(false))
          .catch(err => setSessionLoading(false))
      } else {
        dispatch(getHistory({source, session}))
          .then(res => setSessionLoading(false))
          .catch(err => setSessionLoading(false))
      }
    }
  }


  const handleSend = (e) => {
    e.preventDefault(); // Prevent form submission and page reload
    if(prompt === "") {
      window.alert("Please input the prompt.");
      return;
    }
    let newHistory = [...history, {role: 'user', content: prompt}];
    let username = '', password = '';

    dispatch(updateHistory(newHistory));

    let formData = new FormData();
    formData.append('source', source_name);
    formData.append('session', session_name || '');
    formData.append('cube', cube_name || '');
    formData.append('prompt', prompt);

    if(isAdminLevel === false){
      console.log(source_name)
      const creds = getDbAuthByName(source_name)
      if(creds) {
        username = creds.username;
        password = creds.password;
        formData.append('username', username);
        formData.append('password', password);
      }
      else {
        window.alert('Your database credentials have expired or are not set. Please re-enter your credentials for this database.')
        return;
      }
    }

    setLoading(true);

    dispatch(chatWithSQL(formData))
      .then(res => {
        const assistantContent = {
          role: 'assistant',
          content: res.payload
        }
        newHistory = [...newHistory, assistantContent]
        dispatch(updateHistory(newHistory));
        setLoading(false);
      })
      .catch(err => {
        console.log(err);
        const assistantContent = {
          role: 'assistant',
          content: {
            resultDescription: "Sorry, but I can't generate the data you are asking for",
            error: err
          }
        }
        newHistory = [...newHistory, assistantContent]
        dispatch(updateHistory(newHistory));
        setLoading(false);
      })
    setPrompt("");
  }

  const handleContextMenu = (e, data) => {
    e.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: e.clientX + 2,
            mouseY: e.clientY - 6,
          }
        : null,
    );
    setContextData(data)
  }

  const handleActionMenu = (e) => {
    e.preventDefault();
    setActionMenu(
      actionMenu === null
        ? {
            mouseX: e.clientX + 2,
            mouseY: e.clientY + 26,
          }
        : null,
    );
  }

  const handleActionMenuClose = () => {
    setActionMenu(null);
  }

  const handleEditSession = () => {
    setOpenSession(true);
    setUpdateSession(true);
  }

  const handleRemoveSession = () => {
    if(window.confirm(`Do you really want to remove the session ${session_name}?`)) {
      dispatch(removeSession({source: source_name, session: session_name}))
      .then(res => {
        window.alert("The Session removed successfully!");
        dispatch(getHierarchy());
      })
      .catch(err => {
        console.log(err)
      })
    }
  }

  const handleClose = () => {
    setContextMenu(null);
  };

  const handleShowQuery = () => {
    setQueryOpened(true);
    setContextMenu(null);
  }

  const handleEditQuery = () => {
    console.log(contextData?.sql_query)
    if(contextData?.sql_query)
      dispatch(parseQuery({query: contextData.sql_query}));
    setContextMenu(null);
  }

  const handleDownloadCSV = () => {
    const headers = Object.keys(contextData.result[0]);
    let dummyData = "";
    headers.forEach((item, index) => {
      dummyData+=item;
      if(index !== headers.length-1)
        dummyData+=','
    });
    dummyData+="\n";

    contextData.result.forEach(contextItem => {
      let newLine = "";
      headers.forEach((column, index) => {
        newLine+=contextItem[column];
        if(index !== headers.length-1)
          newLine+=",";
      })
      newLine+="\n";
      dummyData+=newLine;
    })

    const csvContent = `data:text/csv;charset=utf-8,${dummyData}`;
    const encodedURI = encodeURI(csvContent);
    window.open(encodedURI);
    setContextMenu(null);
  }

  const handleCloseQuery = (e) => {
    setQueryOpened(false);
  }

  const handlePowerpoint = (e) => {
    setPptOpened(true);
    setContextMenu(null);
  }

  const handleClosePowerpoint = (e) => {
    setPptOpened(false);
  }
  
  const handlePowerPointOptions = (type, commentary) => {
    console.log(type, commentary)
  }

  const handleExcel = (e) => {
    // const table = convertTableData(contextData.result, numberWithCommas);
    const table = contextData.result
    const ws = utils.json_to_sheet(table);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Data");
    writeFileXLSX(wb, `test_xlsx.xlsx`);
    setContextMenu(null);
  }

    const handleGoogleSheet = (e) => {
    // const table = contextData.result;
    // const config = {
    //   method: 'post',
    //   url: `${API_URI}/export/googlesheet`,
    //   headers: {
    //       'Content-Type': 'application/json'
    //   },
    //   data: JSON.stringify({data: table, type: 'sql', separator, unit, decimal_place:decimalPlace})
    // };

    // setLoading(true);

    // axios.request(config)
    //   .then(res => {
    //     const {success, err} = res.data;
    //     if(success)
    //       window.alert("Successfully exported to GoogleSheet.");
    //     else
    //       window.alert("Error: "+res.data.err)
    //     setLoading(false)
    //     console.log(err)
    //   })
    //   .catch(err => {
    //     console.log(err)
    //     window.alert("Error happened! " + err)
    //     setLoading(false)
    //   })
    
    setContextMenu(null);
  }

  const handlePivottable = (e) => {
    const {result} = contextData;
    const keys = Object.keys(result[0]);
    let legendKey = ''; 
    let dataKeys = [];
    for(let i=0;i<keys.length;i++)
      if(typeof result[1][keys[i]] === 'string' && legendKey === '') 
        legendKey = keys[i];
      else if(typeof result[1][keys[i]] === 'number')
        dataKeys.push(keys[i]);
        
    let dataset = [];
    dataKeys.forEach(key => {
      let dataList = [];
      result.forEach(item => {
        dataList.push({
          label: item[legendKey]===null? "Other":item[legendKey],
          value: item[key]
        });
      })

      dataset.push({
        group: key,
        values: dataList
      })
    })


    // let newHistory = [...history, {role: 'visualization', content: dataset}];

    let newHistory = [...history, {role: 'pivot', content: {keys: dataKeys, dataset: result}}];

    dispatch(updateHistory(newHistory));

    // setChartData(dataset);
    // setVisualOpened(true);
    setContextMenu(null);
  }
  
  // Function to identify the main KPI from numeric columns
  const identifyMainKPI = (numericKeys, result) => {
    if (numericKeys.length === 0) return null;
    if (numericKeys.length === 1) return numericKeys[0];

    // KPI priority keywords - columns with these names are considered more important
    const kpiKeywords = [
      'total_sales', 'sales', 'revenue', 'amount', 'value', 'total', 'sum',
      'units_sold', 'quantity', 'count', 'volume', 'revenue', 'income',
      'profit', 'margin', 'earnings', 'gross', 'net', 'kpi', 'metric'
    ];

    // Score each numeric column
    const scores = numericKeys.map(key => {
      const keyLower = key.toLowerCase();
      let score = 0;

      // Check for KPI keywords in column name
      for (const keyword of kpiKeywords) {
        if (keyLower.includes(keyword)) {
          score += 10;
          break;
        }
      }

      // Calculate average value magnitude (higher values = more important)
      const values = result.map(item => Math.abs(item[key] || 0));
      const avgValue = values.reduce((a, b) => a + b, 0) / values.length;
      score += Math.log10(avgValue + 1); // Log scale to avoid dominance by very large numbers

      // Prefer columns with more variation (standard deviation)
      const mean = avgValue;
      const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
      const stdDev = Math.sqrt(variance);
      score += Math.log10(stdDev + 1);

      return { key, score };
    });

    // Sort by score and return the highest scoring key
    scores.sort((a, b) => b.score - a.score);
    return scores[0].key;
  };

  const handleVisualization = (e) => {
    const {result} = contextData;
    const keys = Object.keys(result[0]);
    let legendKey = ''; 
    let dataKeys = [];
    for(let i=0;i<keys.length;i++)
      if(typeof result[1][keys[i]] === 'string' && legendKey === '') 
        legendKey = keys[i];
      else if(typeof result[1][keys[i]] === 'number')
        dataKeys.push(keys[i]);
    
    // Identify the main KPI instead of using all numeric columns
    const mainKPI = identifyMainKPI(dataKeys, result);
    
    if (!mainKPI) {
      setContextMenu(null);
      return;
    }
        
    let dataset = [];
    // Only create dataset for the main KPI
    let dataList = [];
    result.forEach(item => {
      dataList.push({
        label: item[legendKey]===null? "Other":item[legendKey],
        value: item[mainKPI]
      });
    });

    dataset.push({
      group: mainKPI,
      values: dataList
    });

    let newHistory = [...history, {role: 'visualization', content: dataset}];

    dispatch(updateHistory(newHistory));

    // setChartData(dataset);
    // setVisualOpened(true);
    setContextMenu(null);
  }

  const handleCreateSession = (e) => {
    dispatch(formatHistory());
    dispatch(formatSession());
  }

  const handleSaveSession = (e) => {
    const data = JSON.stringify(history)
    if(session_name === '')
      setOpenSession(true)
    else
      dispatch(saveSession({source: source_name, session: session_name, data}))
        .then(res => {
          window.alert("Chat History saved successfully!");
          dispatch(getHierarchy());
        })
        .catch(err => {
          console.log(err)
        })
  }

  const handleSaveSessionName = (value) => {
    if(updateSession) {
      dispatch(renameSession({source: source_name, origin: session_name, next: value}))
        .then(res => {
          window.alert("Chat History saved successfully!");
          dispatch(getHierarchy());
        })
        .catch(err => {
          console.log(err)
        })
    }
    else {
      const data = JSON.stringify(history)
      dispatch(saveSession({source: source_name, session: value, data}))
        .then(res => {
          window.alert("Chat History saved successfully!");
          dispatch(getHierarchy());
        })
        .catch(err => {
          console.log(err)
        })
    }
    setOpenSession(false);
    setUpdateSession(false);
    setActionMenu(null);
  }

  const handleCloseSessionName = () => {
    setOpenSession(false);
    setUpdateSession(false);
    setActionMenu(null);
  }

  return(
    <Box className="w-full h-full ">
      <Toolbar />
      <Box className="content-height" sx={{padding: 4}}>
        <Card className='h-full flex flex-row border-content' sx={{boxShadow: 'rgba(145, 158, 171, 0.3) 0px 0px 2px 0px, rgba(145, 158, 171, 0.12) 0px 12px 24px -4px'}}>
          <Box className='flex flex-column' sx={{ width: 320, minWidth: 320, flexShrink: 0, borderRight: '1px solid #E5EAEF' }}>
            <Box sx={{padding: 3}}>
              <Typography variant='h6'>History</Typography>
            </Box>
            <Box className='flex-basis relative'>
              {hLoading? (
                <Box className="absolute w-full h-full" sx={{top: 0, left: 0}}>
                  <Box className="relative w-full h-full">
                    <Box className="w-full h-full flex justify-center align-center">
                      <CircularProgress sx={{opacity: 0.7}} />
                    </Box>
                  </Box>
                </Box>
              ) : (
                <SourceTree data={hierarchy} onSelectSession={handleChangeSession}/>
              )}
            </Box>
          </Box>
          <Box className='flex-basis relative' sx={{ minWidth: 0 }}>
            <Box className='flex flex-column h-full'>
              <Box sx={{padding: 3, flexGrow: 0}} className='flex flex-row align-center'>
                <Typography variant='h6' sx={{flexGrow:1, width: 160}}>New Session</Typography>
                <Box
                  className="w-full flex"
                  sx={{
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1
                  }}
                >
                  <CSettingButton
                    sx={{
                      color: 'white',
                      padding: '0.25rem 1rem',
                      background: '#FA896B',
                      '&:hover': {
                        background: '#FF976C'
                      }
                    }}
                    onClick={handleSaveSession}
                  >
                    Save Session
                  </CSettingButton>
                  <CSettingButton
                    sx={{
                      background: '#5D87FF',
                      color: 'white',
                      padding: '0.25rem 1rem',
                      '&:hover': {
                        background: '#6695FF'
                      }
                    }}
                    onClick={handleCreateSession}
                  >
                    New Session
                  </CSettingButton>
                  <CSettingButton
                    sx={{
                      height: 30,
                      width: 30,
                      color: '#404040'
                    }}
                    onClick={handleActionMenu}
                  >
                    <MoreVertOutlinedIcon
                      fontSize='smaller'
                    />
                  </CSettingButton>
                </Box>
              </Box>
              <Divider/>
              <Box className='flex flex-column' sx={{flexGrow: 1, p: 2, overflowX: 'hidden', height: '100%'}}>
                <SimpleBar forceVisible="y" style={{ maxHeight: '100%' }}>
                  <Box className="chat-item">
                    {
                      history.map((item, index) => {
                        if(item.role === 'assistant') 
                          return (          
                            <Box key={`assistant-${index}`} className="chat-item-content assistant-message" onContextMenu={(e) => handleContextMenu(e, item.content)} >
                              {item.content && item.content.resultDescription?item.content.resultDescription: ""}
                              {item.content && item.content.result && item.content.result.length>0 && (
                                <Box className="data-table-container">
                                  <Box className="data-table-header">
                                    📊 Query Results
                                  </Box>
                                  <DataTable data={item.content.result} />
                                </Box>
                              )}
                              <Box className="message-timestamp">
                                {new Date().toLocaleTimeString()}
                              </Box>
                            </Box>)
                        else if(item.role === 'visualization')
                          return (
                            <Box key={`assistant-${index}`} className="chat-item-content assistant-message">
                              <Visualization data={item.content} />
                              <Box className="message-timestamp">
                                {new Date().toLocaleTimeString()}
                              </Box>
                            </Box>
                        )
                        else if(item.role === 'pivot')
                          return (
                            <Box key={`assistant-${index}`} className="chat-item-content assistant-message">
                              <PivotTable data={item.content} />
                              <Box className="message-timestamp">
                                {new Date().toLocaleTimeString()}
                              </Box>
                            </Box>
                          )
                        else
                          return (          
                            <Box key={`user-${index}`} className="chat-item-content user-message">
                              {item.content}
                              <Box className="message-timestamp">
                                {new Date().toLocaleTimeString()}
                              </Box>
                            </Box>)                  
                      })
                    }
                    {loading && (
                      <Box className="chat-item-content assistant-message">
                        <Box className="typing-indicator">
                          <span>AI is thinking</span>
                          <Box className="typing-dots">
                            <Box className="typing-dot"></Box>
                            <Box className="typing-dot"></Box>
                            <Box className="typing-dot"></Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                    {sessionLoading && (
                      <Box className="chat-item-content assistant-message">
                        <Box className="typing-indicator">
                          <span>Loading chat history</span>
                          <Box className="typing-dots">
                            <Box className="typing-dot"></Box>
                            <Box className="typing-dot"></Box>
                            <Box className="typing-dot"></Box>
                          </Box>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </SimpleBar>
              </Box>
              <Divider/>
              <Box sx={{flexGrow: 0}}>
                <Box className="modern-input-container">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InputBase
                      className="modern-input"
                      placeholder="Ask a question about your data..."
                      inputProps={{ 'aria-label': 'ask a question' }}
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend(e);
                        }
                      }}
                      multiline
                      maxRows={4}
                    />
                    <IconButton 
                      className="send-button"
                      onClick={handleSend} 
                      disabled={loading || prompt.trim() === ''}
                      sx={{ 
                        background: '#3b82f6',
                        color: 'white',
                        '&:hover': {
                          background: '#2563eb',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)'
                        },
                        '&:disabled': {
                          opacity: 0.6,
                          transform: 'none'
                        }
                      }}
                    >
                      <SendIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Card>
      </Box>
      <Menu
        open={contextMenu !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleShowQuery}>Show Query</MenuItem>
        <MenuItem onClick={handleEditQuery}>Edit Query</MenuItem>
        <Divider />
        <MenuItem onClick={handleDownloadCSV}>Create CSV</MenuItem>
        <MenuItem onClick={handleExcel}>Create XLSX</MenuItem>
        <MenuItem onClick={handleGoogleSheet}>Create GoogleSheet</MenuItem>
        <Divider />
        <MenuItem onClick={handlePowerpoint}>Create Presentation</MenuItem>
        <MenuItem onClick={handleVisualization}>Create Visualization</MenuItem>
        <MenuItem onClick={handlePivottable}>Create Pivottable</MenuItem>
      </Menu>
      <Menu
        open={actionMenu !== null}
        onClose={handleActionMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          actionMenu !== null
            ? { top: actionMenu.mouseY, left: actionMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleEditSession} disabled={session_name === ''}>Edit</MenuItem>
        <MenuItem onClick={handleRemoveSession} disabled={session_name === ''}>Remove</MenuItem>
      </Menu>
      <TitleModal 
        open={openSession}
        value={session_name}
        handleSave={handleSaveSessionName}
        handleClose={handleCloseSessionName}
      />
      <QueryModal 
        open={queryOpened} 
        handleClose={handleCloseQuery} 
        query={contextData?.sql_query} 
      />
      <PptModal 
        open={pptOpened} 
        data={contextData} 
        handleClose={handleClosePowerpoint} 
        handleOptions={handlePowerPointOptions} 
      />
    </Box>
  );
}